import { createSlice } from '@reduxjs/toolkit';
import { OrderListState } from '../types';
import store from './store';
import { setCommonDataLoader } from './commonSlice';
import {
  getApiCall,
  patchApiCall,
  postApiCall,
  putApiCall,
} from '../api/methods';
import endPoints from '../api/endpoint';
import { notify } from '../Utils/toastify';
import { handleError } from './orderSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../Utils/firebaseConfig';
import { ANALYTICS_EVENTS } from '../helpers/analyticsEvents';

const initialState: OrderListState = {
  orderList: [],
  orderDetails: null,
  pageNo: 1,
  loading: false,
  error: false,
  cancelOrderDetails: null,
  returnItemsList: {},
  updatedShifts: null,
  updatedShiftsLoading: false,
  disputeItems: {},
  orderPermission: {},
  worksheetItems: {},
  worksheetSummary: {},
};
const orderListSlice = createSlice({
  name: 'orderList',
  initialState,
  reducers: {
    setOrderList: (state, action) => {
      const { payload }: any = action;
      state.orderList = payload;
    },
    setOrderDetails: (state, action) => {
      const { payload }: any = action;
      state.orderDetails = payload;
    },
    setOrderLoader: (state, action) => {
      const { payload }: any = action;
      state.loading = payload;
    },
    setUpdatedShiftsLoading: (state, action) => {
      const { payload }: any = action;
      state.updatedShiftsLoading = payload;
    },
    setCancelOrderDetails: (state, action) => {
      const { payload }: any = action;
      state.cancelOrderDetails = payload;
    },
    setReturnItems: (state, action) => {
      const { payload }: any = action;
      state.returnItemsList = payload;
    },
    setUpdatedShifts: (state, action) => {
      const { payload }: any = action;
      state.updatedShifts = payload;
    },
    setDisputeItems: (state, action) => {
      const { payload }: any = action;
      state.disputeItems = payload;
    },
    setOrderPermission: (state, action) => {
      const { payload }: any = action;
      state.orderPermission = payload;
    },
    setWorksheetItems: (state, action) => {
      const { payload }: any = action;
      state.worksheetItems = payload;
    },
    setWorksheetSummary: (state, action) => {
      const { payload }: any = action;
      state.worksheetSummary = payload;
    },
  },
});

export const {
  setOrderList,
  setOrderDetails,
  setOrderLoader,
  setCancelOrderDetails,
  setReturnItems,
  setUpdatedShifts,
  setUpdatedShiftsLoading,
  setDisputeItems,
  setOrderPermission,
  setWorksheetItems,
  setWorksheetSummary,
} = orderListSlice.actions;

export default orderListSlice.reducer;

export const getOrdreTrack = (query: string, cb: any) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));

  getApiCall(
    `${endPoints.order.getOrderTracking + query}`,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        console.log('data', data);
        cb(data);
      }
      dispatch(setCommonDataLoader(false));
    },
    (_e: any) => {
      // handleError(e);
      dispatch(setCommonDataLoader(false));
    },
  );
};

export const fetchOrderDetails = (
  orderId: string,
  dispatch: any,
  cb?: any,
  tabType?: string | any,
  returnTab?: boolean,
) => {
  dispatch(setOrderLoader(true));

  let endpoint = `${endPoints.order.getOrderDetails}/${orderId}`;
  if (!returnTab) {
    endpoint += `?tabType=${tabType}`;
  } else {
    endpoint += `?tabType=${tabType}&returnTab=${returnTab}`;
  }

  getApiCall(
    endpoint,
    (response: any) => {
      const data = response.data?.data;
      if (data && data.orderRes && data.orderRes.length > 0) {
        dispatch(setOrderDetails(data)); // store entire API data
        const chainPermission = cb && cb(data);
        if (!chainPermission) {
          dispatch(setOrderLoader(false));
        }
      } else {
        console.error('No order data found in response');
        dispatch(setOrderLoader(false));
      }
    },
    (error: any) => {
      console.error('Error fetching order details:', error);
      dispatch(setOrderLoader(false));
    },
  );
};
export const updateOrderStatus = (
  data: any,
  cb?: any,
  options?: { silent?: boolean },
) => {
  postApiCall(
    `${endPoints.order.updateOrderStatus}`,
    data,
    (response: any) => {
      cb();
      if (!options?.silent) {
        notify(response.data.message, 'success');
      }
    },
    (error: any) => {
      handleError(error);
    },
  );
};
export const updateOrderVendorStatus = (data: any, cb?: any) => {
  postApiCall(
    `${endPoints.order.updateVendorOrderStatus}`,
    data,
    (response: any) => {
      cb();
      notify(response.data.message, 'success');
    },
    (error: any) => {
      handleError(error);
    },
  );
};
export const updateLocation = (payload: {
  orderId: string;
  setId: string;
  oldSetId: string;
  setName: string;
  locations: {
    latitude: number;
    longitude: number;
    oldLocationId: string;
    locationId: string;
    locationName: string;
  }[];
}): Promise<any> => {
  const apiEndpoint = `${endPoints?.order.updateLocation}`;

  return new Promise((resolve, reject) => {
    putApiCall(
      apiEndpoint,
      payload,
      (response: any) => {
        console.log(response, 'Update location success response');
        notify('Location updated successfully', 'success');
        resolve(response);
      },
      (error: any) => {
        notify('Error while updating location', 'error');
        reject(error);
      },
    );
  });
};

export const reportProblem = (data: any, cb?: () => void) => {
  patchApiCall(
    endPoints.order.reportProblem,
    data,
    (response: any) => {
      notify(
        response.data.message || 'Problem reported successfully',
        'success',
      );
      if (cb) cb();
    },
    (error: any) => {
      notify(error?.data?.message || 'Something went wrong', 'error');
    },
  );
};

export const approveReject = (data: any, cb?: () => void) => {
  putApiCall(
    endPoints.order.approveReject,
    data,
    (response: any) => {
      fetchOrderDetails(data.vendorOrderId, store.dispatch, cb, 'MY_ORDERS');
      notify(
        response.data.message || 'Problem reported successfully',
        'success',
      );
      if (cb) cb();
    },
    (error: any) => {
      handleError(error);
    },
  );
};

export const updateOrderComment = (data: any, cb?: () => void) => {
  postApiCall(
    endPoints.order.addComment,
    data,
    (response: any) => {
      notify(
        response.data.message || 'Comment updated successfully',
        'success',
      );
      if (cb) cb();
    },
    (error: any) => {
      notify(error?.data?.message || 'Something went wrong', 'error');
    },
  );
};

export const getCancelOrderDetails = (orderId: any, cb?: () => void) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  getApiCall(
    endPoints.order.cancelOrderItems + `/${orderId}`,
    (response: any) => {
      if (cb) cb();
      dispatch(setCancelOrderDetails(response.data.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);

      dispatch(setOrderLoader(false));
    },
  );
};

export const cancelOrder = (
  orderIds: any,
  data: any,
  documents: any,
  from: string,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const url = `${endPoints.order.deleteItem}`;

  const payload = {
    ...(from != 'shifts' && {
      orderItemIds: orderIds.map((val: any) => val?._id),
    }),
    ...(from == 'shifts' && {
      shiftIds: orderIds.checkedIds.map((val: any) => val),
    }),
    ...(data && {
      cancelReason: data?.reason?.value,
      otherReason: data?.issues,
    }),
    ...(documents && {
      attachmentUrls: documents?.map((val: any) => ({
        name: val.docName,
        url: val.url,
      })),
    }),
  };
  putApiCall(
    url,
    payload,
    (_response: any) => {
      try {
        if (from === 'shifts') {
          logEvent(analytics, ANALYTICS_EVENTS.ORDER_SHIFTS_CANCELLED, {
            shift_count: payload.shiftIds?.length,
            shift_ids: payload.shiftIds?.join(','),
          });
        } else {
          logEvent(analytics, ANALYTICS_EVENTS.ORDER_CANCELLED);
        }
      } catch (_) {}
      if (cb) cb();
    },
    (error: any) => {
      if (errorCb) errorCb();
      handleError(error);
    },
  );
};

export const getReturnRequestItemsList = (cb?: () => void) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  getApiCall(
    endPoints.order.returnRequestItems,
    (response: any) => {
      if (cb) cb();
      dispatch(setReturnItems(response?.data?.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);

      dispatch(setOrderLoader(false));
    },
  );
};

export const returnOrder = (
  orderIds: any,
  data: any,
  documents: any,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const url = `${endPoints.order.returnItem}`;

  const payload = {
    orderItemIds: orderIds.map(val => val._id),
    returnReason: data.reason,
    returnDescription: data.issues,
    returnAttachmentUrls: documents.map(val => ({
      name: val.docName,
      url: val.url,
    })),
  };
  putApiCall(
    url,
    payload,
    (response: any) => {
      if (
        response?.data?.statusCode === 200 ||
        response?.data?.statusCode === 202
      ) {
        try {
          logEvent(analytics, ANALYTICS_EVENTS.ORDER_RETURNED, {
            reason: data?.reason,
          });
        } catch (_) {}
        if (cb) cb();
        notify(response?.data?.message, 'success');
      }
      if (response?.data?.statusCode === 400) {
        notify(response?.data?.message, 'error');
      }
    },
    (error: any) => {
      notify(error?.data?.message, 'error');
      if (errorCb) {
        errorCb();
      }
      handleError(error);
    },
  );
};

export const getUpdatedShifts = (
  vendorOrderId: string,
  query?: string,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const { dispatch } = store;
  dispatch(setUpdatedShiftsLoading(true));
  const url = `${endPoints.order.getUpdatedShifts}/${vendorOrderId}/order-updates`;
  getApiCall(
    url,
    (response: any) => {
      if (cb) cb();
      dispatch(setUpdatedShifts(response.data.data));
      dispatch(setUpdatedShiftsLoading(false));
    },
    (error: any) => {
      if (errorCb) errorCb();
      dispatch(setUpdatedShiftsLoading(false));
      handleError(error);
    },
  );
};
export const acceptOrRejectOrderUpdates = (
  payload: any,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const url = `${endPoints.order.getUpdatedShifts}/order-updates-approval`;
  putApiCall(
    url,
    payload,
    (_response: any) => {
      if (cb) cb();
    },
    (error: any) => {
      if (errorCb) errorCb();

      handleError(error);
    },
  );
};

export const getReturnedItemsList = (orderId: string, cb?: () => void) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  getApiCall(
    `${endPoints.order.returnRequestItems}?orderId=${orderId}`,
    (response: any) => {
      if (cb) cb();
      dispatch(setReturnItems(response?.data?.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);

      dispatch(setOrderLoader(false));
    },
  );
};

export const cancelRequest = (
  orderIds: any,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const url = `${endPoints.order.cancelReturnRequest}`;

  const payload = {
    orderItemIds: orderIds.map(val => val),
  };
  putApiCall(
    url,
    payload,
    (_response: any) => {
      if (cb) cb();
      notify('Return request cancelled successfully', 'success');
    },
    (error: any) => {
      if (errorCb) errorCb();
      handleError(error);
    },
  );
};

export const raiseADispute = (
  orderIds: any,
  orderId: any,
  cb?: () => void,
  errorCb?: () => void,
) => {
  const url = `${endPoints.order.raiseDispute}`;

  const payload = {
    orderItemIds: orderIds.map(val => val._id),
    orderId: orderId,
  };
  putApiCall(
    url,
    payload,
    (response: any) => {
      if (cb) cb();
      notify(response?.data?.message, 'success');
    },
    (error: any) => {
      if (errorCb) errorCb();
      handleError(error);
    },
  );
};
export const getDisputeItemsList = (orderId: string, cb?: () => void) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  getApiCall(
    `${endPoints.order.raiseDisputeItemList}?orderId=${orderId}`,
    (response: any) => {
      if (cb) cb();
      dispatch(setDisputeItems(response?.data?.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);

      dispatch(setOrderLoader(false));
    },
  );
};

export const getOrderPermission = (
  productionId: string,
  cb?: () => void,
  options?: { skipLoaderStart?: boolean },
) => {
  const { dispatch } = store;
  if (!options?.skipLoaderStart) {
    dispatch(setOrderLoader(true));
  }
  getApiCall(
    `${endPoints.order.orderPermission}?productionId=${productionId}`,
    (response: any) => {
      if (cb) cb();
      dispatch(setOrderPermission(response?.data?.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);
      dispatch(setOrderLoader(false));
    },
  );
};

export const getWorksheetItems = (
  vendorOrderId: string,
  tabType: string,
  entityId?: string,
  entityType?: string,
  fromDate?: string,
  toDate?: string,
  groupBy?: string,
  quickSelect?: string,
  productId?: string,
) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  let url = `${endPoints.order.worksheetItems}?vendorOrderId=${vendorOrderId}&tabType=${tabType}`;

  if (entityId) url += `&entityId=${entityId}`;
  if (entityType) url += `&entityType=${entityType}`;
  if (fromDate) url += `&fromDate=${fromDate}`;
  if (toDate) url += `&toDate=${toDate}`;
  if (groupBy) url += `&groupBy=${groupBy}`;
  if (quickSelect) url += `&quickSelect=${quickSelect}`;
  if (productId) url += `&productId=${productId}`;

  getApiCall(
    url,
    (response: any) => {
      dispatch(setWorksheetItems(response.data.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);
      dispatch(setOrderLoader(false));
    },
  );
};

export const getWorksheetSummary = (vendorOrderId: string) => {
  const { dispatch } = store;
  dispatch(setOrderLoader(true));
  getApiCall(
    `${endPoints.order.worksheetSummary}?vendorOrderId=${vendorOrderId}`,
    (response: any) => {
      dispatch(setWorksheetSummary(response.data.data));
      dispatch(setOrderLoader(false));
    },
    (error: any) => {
      handleError(error);
      dispatch(setOrderLoader(false));
    },
  );
};
